// MessageBanner.tsx
import React, { FunctionComponent, useState, useEffect } from 'react';
import { Snackbar, LinearProgress } from '@mui/material';
import styled from 'styled-components';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';
import Button from './Button';
import Checklist, { ChecklistItemType } from './Checklist';
import Icon from '../global/Icon';

const BannerContent = styled.div<{ $colorMode: ColorModes; $minWidth?: string; $maxWidth?: string; $addRightPadding?: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: fit-content;
    ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
    ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}
    background-color: ${({ $colorMode }) => $colorMode === 'red' ? Colors.redPale : $colorMode === 'light' ? Colors.white : Colors.grey9};
    color: ${({ $colorMode }) => $colorMode === 'red' ? Colors.red : $colorMode === 'light' ? Colors.grey8 : Colors.white};
    border: 1px solid ${({ $colorMode }) => $colorMode === 'red' ? Colors.redPale : $colorMode === 'light' ? Colors.grey1 : Colors.grey8};
    border-radius: 8px;
    padding: 10px 14px;
    margin: 0 5px;
    gap: 8px;
    max-height: calc(100vh - 50px);
    ${({ $addRightPadding }) => $addRightPadding && `
        padding-right: ${$addRightPadding};
    `}
`;

const TitleText = styled.p<{ $colorMode: ColorModes }>`
    position: relative;
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    font-size: 16px;
    line-height: 1.4em;
    color: ${({ $colorMode }) => $colorMode === 'red' ? Colors.red : $colorMode === 'light' ? Colors.grey8 : Colors.white};
    padding: 0;
    margin: 0;
`;

const BodyText = styled.p<{ $colorMode: ColorModes; $hasChecklist: boolean }>`
    font-family: ${Fonts.lexendLight.fontFamily};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: 14px;
    line-height: 1.4em;
    color: ${({ $colorMode }) => $colorMode === 'red' ? Colors.black : $colorMode === 'light' ? Colors.grey8 : Colors.white};
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    padding: 0;
    padding-bottom: ${({ $hasChecklist }) => $hasChecklist ? '4px' : '0'};
    margin: 0;
`;

const IconAndText = styled.div<{ $isLongContent: boolean; $hasChecklist: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;

const HeaderRight = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    opacity: 0.4;
`;

const TextContent = styled.div<{ $hasIconButton?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    width: 100%;
`;

const ChildrenWrapper = styled.div<{ $colorMode: ColorModes }>`
    max-height: 100%;
    overflow-y: auto;
    
    // Move the scrollbar to the right
    margin-right: -10px;
    padding-right: 12px;

    // Scrollbar styling for WebKit browsers (Chrome, Safari, etc.)
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey3 : Colors.grey7};
        border-radius: 4px;
    }
    
    // Scrollbar styling for Firefox
    scrollbar-width: thin;
    scrollbar-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey3 : Colors.grey7} ${({ $colorMode }) => $colorMode === 'light' ? Colors.white : Colors.grey9};
`;

const ContentAndButtonContainer = styled.div<{ $hasIconButton?: boolean }>`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${({ $hasIconButton }) => $hasIconButton ? 20 : 10}px;
`;

const ButtonContainer = styled.div<{ $isLongContent: boolean; $iconOnly?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: ${({ $iconOnly }) => $iconOnly ? 'flex-end' : 'flex-end'};
    align-self: ${({ $isLongContent, $iconOnly }) => $iconOnly ? 'flex-end' : ($isLongContent ? 'flex-end' : 'center')};
    width: ${({ $iconOnly }) => $iconOnly ? 'auto' : 'auto'};
    margin-right: ${({ $iconOnly }) => $iconOnly ? '-4px' : '0'};
    padding-bottom: ${({ $iconOnly }) => $iconOnly ? '2px' : '0'};
`;

const ProgressBarWrapper = styled.div`
    width: 100%;
    margin: 0;
    position: absolute;
    bottom: 0;
    left: 0;
`;

const StyledLinearProgress = styled(LinearProgress)<{ $colorMode: ColorModes }>`
    &.MuiLinearProgress-root {
        height: 2px;
        border-radius: 2px;
        background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey1 : Colors.grey8};
    }
    
    & .MuiLinearProgress-bar {
        background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey4 : Colors.grey6};
    }
`;

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 3px;
    right: 4px;
`;

type ColorModes = 'light' | 'dark' | 'red';

interface PrimaryButtonProps {
    text?: string;
    icon?: string;
    onClick: () => void;
    disabled?: boolean;
}

export type MessageBannerProps = {
    open: boolean;
    onClose?: (event?: React.SyntheticEvent | Event, reason?: string) => void;
    icon?: string;
    titleText?: string;
    bodyText?: string | string[];
    colorMode?: ColorModes;
    primaryButton?: PrimaryButtonProps;
    autoHideDuration?: number | null;
    checklist?: {
        items: ChecklistItemType[];
        checked?: boolean;
        interactive?: boolean;
        onItemChange?: (id: string, checked: boolean) => void;
        showLoading?: boolean;
    };
    includeCloseButton?: boolean;
    onChecklistChange?: (items: ChecklistItemType[]) => void;
    children?: React.ReactNode;
    headerAction?: React.ReactNode; // New prop for header action elements

    anchorOrigin?: {
        vertical: 'top' | 'bottom';
        horizontal: 'left' | 'center' | 'right';
    };
    manualPosition?: boolean;
    style?: React.CSSProperties;
    minWidth?: string;
    maxWidth?: string;
    addRightPadding?: string;
};

const MessageBanner: FunctionComponent<MessageBannerProps> = ({
    open,
    onClose,
    icon,
    titleText,
    bodyText,
    colorMode = 'light',
    primaryButton,
    autoHideDuration = null,
    checklist,
    includeCloseButton = false,
    onChecklistChange,
    children,
    headerAction,

    anchorOrigin = {
        vertical: 'top',
        horizontal: 'center',
    },
    manualPosition = false,
    style,
    minWidth,
    maxWidth,
    addRightPadding,
}) => {
    const [progress, setProgress] = useState(0);
    const [checklistItems, setChecklistItems] = useState<ChecklistItemType[]>(checklist?.items || []);
    
    // Update internal state when checklist items change from props
    useEffect(() => {
        if (checklist?.items) {
            setChecklistItems(checklist.items);
        }
    }, [checklist?.items]);
    
    
    // Determine if content is long based on text length
    const isLongContent = React.useMemo(() => {
        // Only count meaningful bodyText content
        const hasBodyText = bodyText && (
            Array.isArray(bodyText) 
                ? bodyText.length > 0 && bodyText.some(text => text && text.trim().length > 0)
                : bodyText.trim().length > 0
        );
        
        if (hasBodyText) {
            const contentLength = Array.isArray(bodyText) 
                ? bodyText.filter(text => text && text.trim().length > 0).reduce((total, text) => total + text.length, 0) 
                : bodyText?.length || 0;
            return contentLength > 80 || (Array.isArray(bodyText) && bodyText.length > 2);
        }
        
        // Only count checklist if it has items
        return !!(checklist && checklistItems.length > 0);
    }, [bodyText, checklist, checklistItems]);
    
    useEffect(() => {
        if (!open || !autoHideDuration) {
            return;
        }
        
        setProgress(0);
        
        const incrementStep = 100 / (autoHideDuration / 100); // Update every 100ms
        const interval = setInterval(() => {
            setProgress((prevProgress) => {
                if (prevProgress >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prevProgress + incrementStep;
            });
        }, 100);
        
        return () => {
            clearInterval(interval);
        };
    }, [open, autoHideDuration]);

    // Create the banner content component that will be used in both rendering modes
    const bannerContent = (
        <BannerContent 
            id="message-banner-content"
            $colorMode={colorMode} 
            $minWidth={minWidth}
            $maxWidth={maxWidth}
            $addRightPadding={addRightPadding}
            style={{ 
                pointerEvents: 'auto',
                ...style // Apply custom styles when provided
            }}
        >
            {includeCloseButton && onClose && (
                <CloseButtonContainer id="message-banner-close-button">
                    <Button 
                        icon="delete" 
                        color={colorMode === 'dark' ? 'ghost white' : 'ghost'} 
                        size="tiny" 
                        onClick={onClose} 
                    />
                </CloseButtonContainer>
            )}
            <IconAndText id="message-banner-icon-and-text" $isLongContent={isLongContent} $hasChecklist={!!checklist}>
                <HeaderLeft>
                    {icon && (
                        <Icon icon={icon} color={colorMode === 'red' ? Colors.red : colorMode === 'dark' ? Colors.white : Colors.black} size={16}/>
                    )}
                    {titleText && (
                        <TitleText $colorMode={colorMode}>{titleText}</TitleText>
                    )}
                </HeaderLeft>
                {headerAction && (
                    <HeaderRight>
                        {headerAction}
                    </HeaderRight>
                )}
            </IconAndText>
            {/* Create a wrapper div for content and icon-only button */}
            {(
                (bodyText && (
                    Array.isArray(bodyText) 
                        ? bodyText.length > 0 && bodyText.some(text => text && text.trim().length > 0)
                        : bodyText.trim().length > 0
                )) ||
                (checklist && checklistItems.length > 0) ||
                primaryButton
            ) && (
                <ContentAndButtonContainer id="message-banner-content-and-button" $hasIconButton={!!primaryButton && !!primaryButton.icon && !primaryButton.text}>
                    <TextContent id="message-banner-text-content" $hasIconButton={!!primaryButton && !!primaryButton.icon && !primaryButton.text}>
                        {bodyText && (
                            Array.isArray(bodyText) ? (
                                bodyText.length > 0 && bodyText.some(text => text && text.trim().length > 0) ? (
                                    bodyText.map((text, index) => (
                                        <BodyText id={`message-banner-body-text-${index}`} key={index} $colorMode={colorMode} $hasChecklist={!!checklist}>{text}</BodyText>
                                    ))
                                ) : null
                            ) : (
                                bodyText.trim().length > 0 ? (
                                    <BodyText id="message-banner-body-text" $colorMode={colorMode} $hasChecklist={!!checklist}>{bodyText}</BodyText>
                                ) : null
                            )
                        )}
                        {checklist && checklistItems.length > 0 && (
                            <Checklist
                                items={checklistItems}
                                onItemChange={(id, checked) => {
                                    // Update internal state
                                    const updatedItems = checklistItems.map(item => 
                                        item.id === id ? { ...item, checked } : item
                                    );
                                    setChecklistItems(updatedItems);
                                    
                                    // Notify parent component if callback provided
                                    if (checklist.onItemChange) {
                                        checklist.onItemChange(id, checked);
                                    }
                                    
                                    // Notify parent with updated items list if callback provided
                                    if (onChecklistChange) {
                                        onChecklistChange(updatedItems);
                                    }
                                }}
                                colorMode={colorMode === 'red' ? 'dark' : colorMode}
                                showLoading={checklist.showLoading}
                            />
                        )}
                    </TextContent>
                    {/* Place icon-only button inline with content */}
                    {primaryButton && primaryButton.icon && !primaryButton.text && (
                        <ButtonContainer 
                            id="message-banner-icon-button"
                            $isLongContent={false} 
                            $iconOnly={true}
                        >
                            <Button
                                icon={primaryButton.icon}
                                color={colorMode === 'light' ? 'black' : 'white'}
                                size="tiny"
                                onClick={primaryButton.onClick}
                                disabled={primaryButton.disabled}
                            />
                        </ButtonContainer>
                    )}
                </ContentAndButtonContainer>
            )}

            {children && React.Children.count(children) > 0 && (
                <ChildrenWrapper id="message-banner-children" $colorMode={colorMode}>
                    {children}
                </ChildrenWrapper>
            )}
            
            {/* Keep text button below content */}
            {primaryButton && primaryButton.text && (
                <ButtonContainer 
                    id="message-banner-text-button"
                    $isLongContent={isLongContent} 
                    $iconOnly={false}
                >
                    <Button
                        text={primaryButton.text}
                        icon={primaryButton.icon}
                        color={colorMode === 'dark' ? 'white' : 'black'}
                        size="tiny"
                        onClick={primaryButton.onClick}
                        disabled={primaryButton.disabled}
                    />
                </ButtonContainer>
            )}
            {autoHideDuration !== null && (
                <ProgressBarWrapper id="message-banner-progress">
                    <StyledLinearProgress 
                        $colorMode={colorMode === 'dark' ? 'dark' : 'light'} 
                        variant="determinate" 
                        value={progress} 
                    />
                </ProgressBarWrapper>
            )}
        </BannerContent>
    );

    // Conditionally render based on manualPosition flag
    if (manualPosition) {
        // When manually positioned, only render the banner content if open is true
        return open ? bannerContent : null;
    }

    // Default behavior using Snackbar for automatic positioning
    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={includeCloseButton ? onClose : (event, reason) => {
                // Only close automatically (timeout) or when explicitly called
                // Prevent closing on clickaway if includeCloseButton is false
                if (reason !== 'clickaway') {
                    onClose?.(event, reason);
                }
            }}
            anchorOrigin={anchorOrigin}
            // Fix for home button not being clickable on mobile
            // This prevents the Snackbar from creating an overlay that blocks other UI elements
            style={{ pointerEvents: 'none' }}
            ContentProps={{
                style: { pointerEvents: 'auto' }
            }}
        >
            {bannerContent}
        </Snackbar>
    );
};

export default MessageBanner;